import Meeting from '../meeting/meeting';
import DeviceUtility from '../helpers/device';
import { IMediaClient } from '../interfaces/IMediaClient';
import { MeetingSettings } from '../types/MeetingSettings';
import { ref } from 'vue';

class MediaAccessValidationResult {
    permissionRequired: boolean;
    userInteractionRequired: boolean | null;
    userMedia: MediaStream | null;
    audioContext: AudioContext | null;

    constructor(permissionRequired: boolean, userInteractionRequired: boolean | null, userMedia: MediaStream | null, audioContext: AudioContext | null) {
        this.permissionRequired = permissionRequired;
        this.userInteractionRequired = userInteractionRequired;
        this.userMedia = userMedia;
        this.audioContext = audioContext;
    }
}

const _mediaClient = ref(null as IMediaClient | null);
const _meetingSettings = ref(null as MeetingSettings | null);
const _mediaAccessValidationResult = ref(null as MediaAccessValidationResult | null)

export const useMeetingConfiguration = () => {

    const getMediaClient = () => {
        if (_mediaClient.value) {
            return _mediaClient.value;
        } else {
            return undefined
        }
    }
    const setMediaClient = (mc: IMediaClient) => {
        (window as any).mediaClient = mc; // for debugging purposes
        _mediaClient.value = mc;
    }

    const getPingUrl = () => {
        if (_meetingSettings.value) {
            return _meetingSettings.value.pingUrl;
        } else {
            throw new Error('Meeting settings are not specified. Use setSettings to set them.');
        } 
    }

    const getSettings = () => {
        if (_meetingSettings.value) {
            return _meetingSettings.value;
        } else {
            throw new Error('Meeting settings are not specified. Use setSettings to set them.');
        }
    }

    const setSettings = (settings: MeetingSettings) => {
        if (settings instanceof MeetingSettings) {
            _meetingSettings.value = settings;
        } else {
            throw new Error('Invalid settings. It must be an instance of MeetingSettings.');
        }
    }

    

    
    const validateMediaAccess = async () : Promise<MediaAccessValidationResult> => {
       // attempt the media access; this allows a single shot access to the camera and microphone
        // if we fail, we will show the permissions screen
        const settings = getSettings()
        let userMedia: MediaStream | null = null;
        try{
            userMedia = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        }catch(e){
            if(settings.isCameraRequired){
                // if the camera is required and we fail to access it, we need to show the permissions screen
                _mediaAccessValidationResult.value = new MediaAccessValidationResult(true, null, null, null)
                return _mediaAccessValidationResult.value
            }else{
                // if the camera is not required, we can try to access the microphone only
                // this is to allow the user to join the call without a camera
                try{
                    userMedia = await navigator.mediaDevices.getUserMedia({ video: false, audio: true })
                }catch(e){
                    _mediaAccessValidationResult.value = new MediaAccessValidationResult(true, null, null, null)
                    return _mediaAccessValidationResult.value
                }
            }
        }

        // if the audio context is suspended, we need to show the join panel
        // this is an issue in Safari on Mac specifically, nowhere else
        const audioContext = new AudioContext()
        await audioContext.resume()
        const device = Meeting.detectMobileDevice()
        // if the audio context is suspended, we need to show the join panel
        // this is an issue in Safari on Mac specifically, nowhere else,
        // because it plays the context but still fails to play a moment later, soooo we ignore that
        // case. The extra check on the device is for iPad Pro, which is a desktop like mobile device
        if(audioContext.state == 'suspended' || (DeviceUtility.isMac && DeviceUtility.isSafari && !device.isDesktopLikeMobileDevice)){
            _mediaAccessValidationResult.value = new MediaAccessValidationResult(false, true, userMedia, audioContext)
            return _mediaAccessValidationResult.value
        }
        _mediaAccessValidationResult.value = new MediaAccessValidationResult(false, false, userMedia, audioContext)
        return _mediaAccessValidationResult.value
    }

    const clearMediaAccessValidationResult = (audio: boolean = true, video: boolean = true) => {
        if(audio){
            _mediaAccessValidationResult.value?.userMedia?.getAudioTracks().forEach(track => track.stop())
        }
        if(video){
            _mediaAccessValidationResult.value?.userMedia?.getVideoTracks().forEach(track => track.stop())
        }
        _mediaAccessValidationResult.value = null
    }

    return {
        getMediaClient,
        setMediaClient,
        getPingUrl,
        getSettings,
        setSettings,
        validateMediaAccess,
        clearMediaAccessValidationResult
    };
}
export default useMeetingConfiguration;